// Review for a PRATUS AI automation rule draft: the real rule editor, pre-filled
// with the draft, beside a small chat that revises the form in place. Saving
// from the editor (after its enable prompt) accepts this item with the saved
// payload; chat turns stay in this modal.
import React, { useRef, useState } from 'react';
import { SendIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { RuleEditorSteps } from 'components/MapPage/CorporateViewTabs/EventsTab/RuleEditorSteps';
import type { RuleDraftPayload } from 'components/MapPage/CorporateViewTabs/EventsTab/ruleFormModel';
import { useReviseAutomationRuleDraft } from 'hooks/orgEvents/orgEventAutomationHooks';
import { currentRulePayload } from '../automationRuleReviewModel';
import type { ItemState, PendingConfirmation } from '../types';

type ChatTurn = {
  role: 'user' | 'assistant';
  content: string;
  errors?: string[];
};

const HISTORY_TURNS = 6;

interface Props {
  item: PendingConfirmation;
  state?: ItemState;
  onSave: (payload: RuleDraftPayload) => void | Promise<void>;
}

const AutomationRuleReview: React.FC<Props> = ({ item, state, onSave }) => {
  const [initialPayload] = useState(() =>
    currentRulePayload(item, state?.edits)
  );
  const payloadRef = useRef<(() => RuleDraftPayload) | null>(null);
  const [external, setExternal] = useState<{
    payload: RuleDraftPayload;
    nonce: number;
  }>();
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [message, setMessage] = useState('');
  const revise = useReviseAutomationRuleDraft();

  const send = async () => {
    const text = message.trim();
    const rule = payloadRef.current?.() ?? initialPayload;
    if (!text || revise.isPending) return;
    const history = turns
      .slice(-HISTORY_TURNS)
      .map(({ role, content }) => ({ role, content }));
    setTurns((prev) => [...prev, { role: 'user', content: text }]);
    setMessage('');
    try {
      const result = await revise.mutateAsync({ rule, message: text, history });
      if (result.rule) {
        const revised = result.rule;
        setExternal((prev) => ({
          payload: revised,
          nonce: (prev?.nonce ?? 0) + 1,
        }));
      }
      setTurns((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: result.reply,
          errors: result.errors?.length ? result.errors : undefined,
        },
      ]);
    } catch {
      setTurns((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'I could not revise the rule right now. Try again.',
          errors: ['The request failed.'],
        },
      ]);
    }
  };

  return (
    <div
      className="tw:flex tw:h-[min(68vh,44rem)] tw:min-h-0 tw:gap-4"
      data-testid="automation-rule-review"
    >
      <div className="tw:flex tw:min-h-0 tw:min-w-0 tw:flex-1 tw:flex-col">
        <RuleEditorSteps
          rule={null}
          onClose={() => undefined}
          initialPayload={initialPayload}
          externalPayload={external}
          payloadRef={payloadRef}
          onSubmitPayload={onSave}
          hideCancel
        />
      </div>

      <aside
        aria-label="Chat with PRATUS AI about this rule"
        className="tw:flex tw:w-72 tw:shrink-0 tw:flex-col tw:gap-2 tw:border-l tw:border-border tw:pl-4"
      >
        <p className="tw:m-0 tw:text-sm tw:font-medium tw:text-foreground">
          Ask PRATUS AI to change this rule
        </p>
        <div className="tw:min-h-0 tw:flex-1 tw:space-y-2 tw:overflow-y-auto">
          {turns.length === 0 ? (
            <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
              For example: &ldquo;make it Cat 4 and notify the Ops
              roster&rdquo;. Your edits in the form are kept.
            </p>
          ) : (
            turns.map((turn, index) => (
              <div
                key={index}
                className={
                  turn.role === 'user'
                    ? 'tw:ml-6 tw:rounded-lg tw:bg-muted tw:px-3 tw:py-2 tw:text-sm tw:text-foreground'
                    : 'tw:mr-6 tw:rounded-lg tw:border tw:border-border tw:px-3 tw:py-2 tw:text-sm tw:text-foreground'
                }
              >
                <p className="tw:m-0 tw:whitespace-pre-wrap">{turn.content}</p>
                {turn.errors?.map((error) => (
                  <p
                    key={error}
                    className="tw:m-0 tw:mt-1 tw:text-xs tw:text-destructive"
                  >
                    {error}
                  </p>
                ))}
              </div>
            ))
          )}
          {revise.isPending ? (
            <div className="tw:flex tw:items-center tw:gap-2 tw:text-xs tw:text-muted-foreground">
              <Spinner className="tw:size-3.5" />
              Revising the rule…
            </div>
          ) : null}
        </div>
        <form
          className="tw:flex tw:shrink-0 tw:flex-col tw:gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
        >
          <Textarea
            rows={3}
            value={message}
            placeholder="Describe a change…"
            aria-label="Describe a change to the rule"
            onChange={(event) => setMessage(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void send();
              }
            }}
          />
          <Button
            type="submit"
            size="sm"
            className="tw:gap-1 tw:self-end"
            disabled={!message.trim() || revise.isPending}
          >
            <SendIcon className="tw:size-4" />
            Send
          </Button>
        </form>
      </aside>
    </div>
  );
};

export default AutomationRuleReview;
